import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Card, Input, Button, useIsMobile } from '../components/ui';
import api from '../utils/api';

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const validate = () => {
    const errs = {};
    if (password.length < 8) errs.password = 'Minimum 8 characters.';
    if (confirm !== password) errs.confirm = "Passwords don't match.";
    setFieldErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setLoading(true); setError('');
    try {
      await api.post('/auth/reset-password', { token, new_password: password });
      setDone(true);
      setTimeout(() => navigate('/login'), 2500);
    } catch (e) {
      setError(e.response?.data?.detail || 'This link is invalid or has expired.');
    } finally {
      setLoading(false);
    }
  };

  // Height comes from .bb-auth-shell so the mobile bottom nav is accounted for.
  const wrapperStyle = {
    background: 'var(--gray-50)',
    alignItems: 'center', justifyContent: 'center',
    padding: isMobile ? '24px 16px' : '48px 16px',
  };

  if (!token) {
    return (
      <div className="bb-auth-shell" style={wrapperStyle}>
        <Card style={{ maxWidth: '400px', width: '100%', textAlign: 'center' }}>
          <div style={{ fontSize: '40px', marginBottom: '12px' }}>⚠️</div>
          <div style={{ fontSize: '17px', fontWeight: 700, color: 'var(--gray-900)', marginBottom: '8px' }}>Missing reset link</div>
          <p style={{ fontSize: '13.5px', color: 'var(--gray-500)', marginBottom: '20px', lineHeight: 1.6 }}>
            This page needs a reset token from the link in your email.
          </p>
          <Link to="/forgot-password"><Button variant="primary" fullWidth>Request a new link</Button></Link>
        </Card>
      </div>
    );
  }

  if (done) {
    return (
      <div className="bb-auth-shell" style={wrapperStyle}>
        <Card style={{ maxWidth: '400px', width: '100%', textAlign: 'center' }}>
          <div style={{ fontSize: '40px', marginBottom: '12px' }}>✅</div>
          <div style={{ fontSize: '17px', fontWeight: 700, color: 'var(--gray-900)', marginBottom: '8px' }}>Password updated</div>
          <p style={{ fontSize: '13.5px', color: 'var(--gray-500)', marginBottom: '20px', lineHeight: 1.6 }}>
            Taking you to sign in…
          </p>
          <Link to="/login"><Button variant="secondary" fullWidth>Sign in now</Button></Link>
        </Card>
      </div>
    );
  }

  return (
    <div className="bb-auth-shell" style={wrapperStyle}>
      <Card style={{ maxWidth: '400px', width: '100%', animation: 'bb-fadeUp 0.5s ease both' }}>
        <div style={{ marginBottom: '22px' }}>
          <div style={{ fontSize: '17px', fontWeight: 700, color: 'var(--gray-900)', marginBottom: '6px' }}>Choose a new password</div>
          <p style={{ fontSize: '13.5px', color: 'var(--gray-500)', lineHeight: 1.5 }}>
            This link works once and expires 30 minutes after it was sent.
          </p>
        </div>

        <Input
          label="New password"
          type="password"
          autoFocus
          placeholder="Min 8 characters"
          value={password}
          onChange={e => { setFieldErrors(f => ({ ...f, password: undefined })); setPassword(e.target.value); }}
          error={fieldErrors.password}
        />
        <Input
          label="Confirm new password"
          type="password"
          placeholder="Re-enter your new password"
          value={confirm}
          onChange={e => { setFieldErrors(f => ({ ...f, confirm: undefined })); setConfirm(e.target.value); }}
          onKeyDown={e => e.key === 'Enter' && handleSubmit()}
          error={fieldErrors.confirm}
        />

        {error && (
          <div style={{
            padding: '11px 14px', borderRadius: 'var(--radius)', background: 'var(--red-light)',
            border: '1px solid var(--red-mid)', color: 'var(--red-dark)', fontSize: '13px',
            fontWeight: 500, marginBottom: '14px', lineHeight: 1.5,
          }}>
            {error}{' '}
            <Link to="/forgot-password" style={{ fontWeight: 700, color: 'var(--red-dark)', textDecoration: 'underline' }}>
              Request a new link
            </Link>
          </div>
        )}

        <Button variant="primary" fullWidth loading={loading} onClick={handleSubmit}>
          Update password
        </Button>
      </Card>
    </div>
  );
}
